// ── 흐름 설정 — 매입 최소 표시 금액 + 매달 고정 지출 ─────────────────────────
// ① 최소 표시 금액: 본사 매입·샘이콜론 매입의 바닥값을 각각 정한다.
//    실제 매입 합이 이 금액보다 작으면 이 금액으로 보이고, 넘으면 실제 금액이 보인다.
// ② 매달 고정 지출(2026-10-13): 기간 동안 매달 나가는 돈에 더할 금액 — FixedOutflowSettings.
// 저장 버튼 하나로 바뀐 쪽만 보낸다(둘 다 그대로면 보내지 않는다).
// 금액 입력은 type="text" + inputMode="numeric" — number 의 화살표(spinner) 금지 규칙.

import React, { useEffect, useState } from 'react';
import Modal from '../../../components/Modal';
import { apiClient } from '../../../api/apiClient';
import { todayKST } from '../../../utils/date';
import { fmtAmount } from './constants';
import FixedOutflowSettings, { toEditRows, toPayloadRows, fixedRowsError } from './FixedOutflowSettings';
import { stdBlackBtn, stdOutlineBtn } from '../adminlayout/designStandard';

const FIELDS = [
  { key: 'purchasePine', label: '매입 관리' },
  { key: 'purchaseSemecolon', label: '샘이콜론 매입 관리' },
];

const onlyDigits = (v) => String(v).replace(/[^0-9]/g, '');
// 매달 고정 지출 상태 — rev: 서버 판(남이 먼저 저장했는지 확인용), initial: 연 때 모양(바뀌었는지 비교용)
const EMPTY_FIXED = { rev: null, rows: [], initial: '[]', loadFailed: false };

export default function FloorSettingsModal({ open, onClose, onSaved, showToast }) {
  const [draft, setDraft] = useState({ purchasePine: '', purchaseSemecolon: '' });
  const [loadedFloors, setLoadedFloors] = useState(null);
  const [fixed, setFixed] = useState(EMPTY_FIXED);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setLoading(true);
    (async () => {
      const [res, fx] = await Promise.all([
        apiClient.get('/api/admin/cashflow/settings'),
        apiClient.get('/api/admin/cashflow/fixed-outflows'),
      ]);
      if (!active) return;
      setLoading(false);
      // 매달 고정 지출 — 못 읽으면 그 칸만 잠근다(빈 목록을 저장해 기존 줄을 지우지 않게)
      const fd = fx?.ok && fx.data?.success ? fx.data.data : null;
      if (fd && Number.isInteger(fd.rev) && Array.isArray(fd.rows)) {
        const rows = toEditRows(fd.rows);
        setFixed({ rev: fd.rev, rows, initial: JSON.stringify(toPayloadRows(rows)), loadFailed: false });
      } else {
        setFixed({ ...EMPTY_FIXED, loadFailed: true });
      }
      if (!res.ok || !res.data?.success) {
        // 칸을 비운다 — 지난번에 열 때 값이 남아 있으면 '직접 적은 값'으로 보고 옛 금액을 다시 보내게 된다
        setDraft({ purchasePine: '', purchaseSemecolon: '' });
        setLoadedFloors(null);
        showToast?.(res.data?.message || '설정을 불러오지 못했습니다.', 'error');
        return;
      }
      const d = res.data.data || {};
      const next = {
        purchasePine: String(d.purchasePine ?? ''),
        purchaseSemecolon: String(d.purchaseSemecolon ?? ''),
      };
      setDraft(next);
      setLoadedFloors(next);
    })();
    return () => { active = false; };
  }, [open, showToast]);

  async function save() {
    // 최소 표시 금액은 고쳤을 때만 보낸다(못 읽었으면 직접 적었을 때만)
    const floorsChanged = loadedFloors
      ? FIELDS.some((f) => draft[f.key] !== loadedFloors[f.key])
      : FIELDS.some((f) => draft[f.key] !== '');
    if (floorsChanged) {
      for (const f of FIELDS) {
        if (draft[f.key] === '') {
          showToast?.(`${f.label} 금액을 입력해 주세요.`, 'error');
          return;
        }
      }
    }
    const fixedPayload = fixed.loadFailed ? null : toPayloadRows(fixed.rows);
    const fixedChanged = Boolean(fixedPayload) && JSON.stringify(fixedPayload) !== fixed.initial;
    if (fixedChanged) {
      const err = fixedRowsError(fixed.rows);
      if (err) {
        showToast?.(err, 'error');
        return;
      }
    }
    if (!floorsChanged && !fixedChanged) {
      showToast?.('변경된 값이 없습니다.', 'success');
      onClose?.();
      return;
    }

    setSaving(true);
    const done = [];
    if (floorsChanged) {
      const res = await apiClient.put('/api/admin/cashflow/settings', {
        purchasePine: Number(draft.purchasePine),
        purchaseSemecolon: Number(draft.purchaseSemecolon),
      });
      if (!res.ok || !res.data?.success) {
        setSaving(false);
        showToast?.(res.data?.message || '저장에 실패했습니다.', 'error');
        return;
      }
      done.push(res.data.message || '최소 표시 금액을 저장했습니다.');
      setLoadedFloors({ ...draft }); // 아래가 실패해 다시 누를 때 또 보내지 않게
    }
    if (fixedChanged) {
      const res = await apiClient.put('/api/admin/cashflow/fixed-outflows', { rev: fixed.rev, rows: fixedPayload });
      if (!res.ok || !res.data?.success) {
        setSaving(false);
        const why = res.data?.message || '저장에 실패했습니다.';
        showToast?.(done.length ? `최소 표시 금액은 저장했지만 매달 고정 지출은 저장하지 못했습니다. ${why}` : why, 'error');
        if (done.length) onSaved?.();
        return;
      }
      done.push(res.data.message || '매달 고정 지출을 저장했습니다.');
    }
    setSaving(false);
    showToast?.(done.length > 1 ? '최소 표시 금액과 매달 고정 지출을 저장했습니다.' : done[0], 'success');
    onSaved?.();
    onClose?.();
  }

  return (
    <Modal
      open={open}
      title="흐름 설정"
      icon="⚙️"
      onClose={onClose}
      width="min(480px, calc(100vw - 48px))"
      footer={(
        <div style={footerRow}>
          <button onClick={onClose} style={stdOutlineBtn} disabled={saving}>취소</button>
          <button onClick={save} style={stdBlackBtn} disabled={saving || loading}>
            {saving ? '저장 중...' : '저장'}
          </button>
        </div>
      )}
    >
      <section>
        <h4 style={sectionTitle}>최소 표시 금액</h4>
        <div style={introStyle}>
          아래 두 항목은 월초에 매입이 아직 올라오지 않아 0원으로 보입니다.
          실제 금액이 여기 적은 금액보다 적으면 <b>이 금액으로</b> 보이고,
          넘어서면 <b>실제 금액</b>이 보입니다. 나가는 돈 합계와 순흐름에도 같이 반영됩니다.
        </div>
        {FIELDS.map((f) => (
          <div key={f.key} style={fieldRow}>
            <label htmlFor={`floor-${f.key}`} style={labelStyle}>{f.label}</label>
            <div style={inputWrap}>
              <input
                id={`floor-${f.key}`}
                type="text"
                inputMode="numeric"
                value={draft[f.key]}
                onChange={(e) => setDraft({ ...draft, [f.key]: onlyDigits(e.target.value) })}
                style={inputStyle}
                disabled={loading || saving}
              />
              <span style={unitStyle}>원</span>
            </div>
            <div style={hintStyle}>
              {draft[f.key] === '' ? ' ' : `${fmtAmount(draft[f.key])}원`}
            </div>
          </div>
        ))}
      </section>
      <hr style={dividerStyle} />
      <FixedOutflowSettings
        rows={fixed.rows}
        onChange={(rows) => setFixed((prev) => ({ ...prev, rows }))}
        disabled={loading || saving}
        defaultYm={todayKST().slice(0, 7)}
        loadFailed={fixed.loadFailed && !loading}
      />
    </Modal>
  );
}

const sectionTitle = { margin: '0 0 6px', fontSize: 14, fontWeight: 800, color: 'var(--text)' };
const introStyle = { fontSize: 13, color: 'var(--text-sub)', lineHeight: 1.6, marginBottom: 16 };
const fieldRow = { marginBottom: 14 };
const labelStyle = { display: 'block', fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 6 };
const inputWrap = { display: 'flex', alignItems: 'center', gap: 6 };
const inputStyle = { flex: 1, minWidth: 0, padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 14, background: 'var(--bg-card)', color: 'var(--text)', textAlign: 'right', fontVariantNumeric: 'tabular-nums', boxSizing: 'border-box' };
const unitStyle = { fontSize: 13, color: 'var(--text-sub)', flexShrink: 0 };
const hintStyle = { fontSize: 12, color: 'var(--text-muted)', textAlign: 'right', marginTop: 4, minHeight: 16 };
const dividerStyle = { border: 'none', borderTop: '1px solid var(--border)', margin: '6px 0 16px' };
const footerRow = { display: 'flex', gap: 8, justifyContent: 'flex-end' };
